import { GET_PROFILE } from "./user-detail.query";
import { IUserDetailResponse, IUserItemResponse } from "./user-detail.interface";
import { useDispatch } from "react-redux";
import { useCallback, useEffect } from "react";
import { useApolloClient, useQuery } from "@apollo/client";
import { userActions } from "../../state/user";

const storeProfile = (dispatch: ReturnType<typeof useDispatch>, response?: IUserDetailResponse) => {
  if (response?.user) {
    dispatch(userActions.setUserProfile(response.user));
    dispatch(userActions.setEmailSignIn(response.user.email));
  }
};

// Fetches the profile and stores it in Redux. Resolves with the fresh user, or
// null when the request failed (the global errorLink already reports it).
//
// Uses `client.query` rather than `useLazyQuery` on purpose: the lazy-query
// promise only settles while its host component stays mounted, so a caller
// that navigates away mid-flight would never see the refreshed profile.
export const useRefreshProfile = () => {
  const client = useApolloClient();
  const dispatch = useDispatch();

  return useCallback(async (): Promise<IUserItemResponse | null> => {
    try {
      const { data } = await client.query<{ getProfile: IUserDetailResponse }>({
        query: GET_PROFILE,
        fetchPolicy: "network-only",
      });

      storeProfile(dispatch, data?.getProfile);

      return data?.getProfile?.user ?? null;
    } catch {
      return null;
    }
  }, [client, dispatch]);
};

export const useFetchUserDetailLazy = useRefreshProfile;

export const useUserDetail = () => {
  const dispatch = useDispatch();
  const { data, loading } = useQuery<{ getProfile: IUserDetailResponse }>(GET_PROFILE);

  useEffect(() => {
    storeProfile(dispatch, data?.getProfile);
  }, [data]);

  return {
    user: data?.getProfile.user,
    loading,
  };
};
